/**
 * Endoscopy anatomy + lesion taxonomy.
 *
 * Covers the clinical endoscopy families the workstation is expected
 * to handle. Procedures are a flat enum (not a tree) because that's
 * how the DICOM modality/description maps in practice — a study is
 * filed as "EGD" or "Bronchoscopy", not "upper GI › EGD".
 *
 *   EGD             — oesophagogastroduodenoscopy (upper GI)
 *   Colonoscopy     — full lower GI to caecum / terminal ileum
 *   Sigmoidoscopy   — distal colon only, rectum through descending
 *   Enteroscopy     — small-bowel, balloon or spiral
 *   CapsuleEndoscopy— wireless capsule, small-bowel survey
 *   ERCP            — biliary / pancreatic ductal evaluation
 *   EUS             — endoscopic ultrasound (upper-GI access)
 *   Bronchoscopy    — central airways + lobar/segmental bronchi
 *   Cystoscopy      — urethra, bladder, ureteric orifices
 *   Hysteroscopy    — endocervix, uterine cavity, tubal ostia
 *   Laryngoscopy    — supraglottis / glottis / subglottis
 *
 * Landmark vocabularies are clinically-canonical where a standard
 * exists (GastroHUN for EGD, WEO MST 3.0 for colonoscopy, the ATS
 * bronchial nomenclature for bronchoscopy) and editorial-consensus
 * for the rest, based on the anatomical regions a report is
 * expected to enumerate.
 */

/** Every procedure the viewer knows about. */
export type ProcedureType =
  | "EGD"
  | "Colonoscopy"
  | "Sigmoidoscopy"
  | "Enteroscopy"
  | "CapsuleEndoscopy"
  | "ERCP"
  | "EUS"
  | "Bronchoscopy"
  | "Cystoscopy"
  | "Hysteroscopy"
  | "Laryngoscopy";

export const PROCEDURE_TYPES: ProcedureType[] = [
  "EGD", "Colonoscopy", "Sigmoidoscopy", "Enteroscopy", "CapsuleEndoscopy",
  "ERCP", "EUS", "Bronchoscopy", "Cystoscopy", "Hysteroscopy", "Laryngoscopy",
];

/** Human-readable name for the procedure picker dropdown. */
export const PROCEDURE_LABEL: Record<ProcedureType, string> = {
  EGD:              "EGD (upper GI)",
  Colonoscopy:      "Colonoscopy",
  Sigmoidoscopy:    "Sigmoidoscopy",
  Enteroscopy:      "Enteroscopy (small bowel)",
  CapsuleEndoscopy: "Capsule endoscopy",
  ERCP:             "ERCP",
  EUS:              "Endoscopic ultrasound",
  Bronchoscopy:     "Bronchoscopy",
  Cystoscopy:       "Cystoscopy",
  Hysteroscopy:     "Hysteroscopy",
  Laryngoscopy:     "Laryngoscopy",
};

/** Token-to-procedure map. Match order matters — longer / more
 *  specific tokens first so "sigmoid" wins over "colo", "ercp"
 *  wins over "rcp". Keys are matched as substrings against a
 *  normalised signal string (lower-case, `^` boundary preserved
 *  so "VPD^ENDO^COLO" matches "^colo"). */
const PROC_TOKENS: Array<[string, ProcedureType]> = [
  ["colonoscopy",        "Colonoscopy"],
  ["colono",             "Colonoscopy"],
  ["^colo",              "Colonoscopy"],
  ["c-scope",            "Colonoscopy"],
  ["sigmoidoscopy",      "Sigmoidoscopy"],
  ["sigmoid",            "Sigmoidoscopy"],
  ["^sig",               "Sigmoidoscopy"],
  ["capsule",            "CapsuleEndoscopy"],
  ["pillcam",            "CapsuleEndoscopy"],
  ["^caps",              "CapsuleEndoscopy"],
  ["enteroscopy",        "Enteroscopy"],
  ["enteros",            "Enteroscopy"],
  ["small bowel",        "Enteroscopy"],
  ["^ent",               "Enteroscopy"],
  ["ercp",               "ERCP"],
  ["^ercp",              "ERCP"],
  ["endoscopic ultrasound", "EUS"],
  ["^eus",               "EUS"],
  [" eus ",              "EUS"],
  ["bronchoscopy",       "Bronchoscopy"],
  ["bronch",             "Bronchoscopy"],
  ["^bronc",             "Bronchoscopy"],
  ["cystoscopy",         "Cystoscopy"],
  ["cysto",              "Cystoscopy"],
  ["urethro",            "Cystoscopy"],
  ["^cysto",             "Cystoscopy"],
  ["hysteroscopy",       "Hysteroscopy"],
  ["hystero",            "Hysteroscopy"],
  ["^hys",               "Hysteroscopy"],
  ["laryngoscopy",       "Laryngoscopy"],
  ["laryng",             "Laryngoscopy"],
  ["^lar",               "Laryngoscopy"],
  // EGD last: `^egd` is a cheap exact-token match for PatientName-
  // style fields, and "upper gi" + "gastro" cover the long form.
  ["^egd",               "EGD"],
  ["egd",                "EGD"],
  ["gastroduodenoscopy", "EGD"],
  ["upper gi",           "EGD"],
  ["oesophagogastro",    "EGD"],
  ["esophagogastro",     "EGD"],
];

/** Guess the procedure from one or more DICOM signals. Accepts any
 *  number of strings (StudyDescription, SeriesDescription, Patient
 *  Name, ProtocolName, etc.) and returns the first procedure whose
 *  token matches any signal. Falls back to EGD — the most frequent
 *  endoscopy type — so the UI always has a non-empty anatomy list.
 *
 *  Note on PatientName: many modality-worklist setups stuff a short
 *  procedure code into the third PN component (e.g. "VPD^ENDO^COLO"),
 *  which is why the token list includes `^colo`, `^egd`, etc. */
export function guessProcedure(...signals: Array<string | undefined | null>): ProcedureType {
  const blob = signals.filter(Boolean).join(" · ").toLowerCase();
  if (!blob) return "EGD";
  for (const [tok, proc] of PROC_TOKENS) if (blob.includes(tok)) return proc;
  return "EGD";
}

export interface AnatomyLabel {
  id: string;
  /** UI label. */
  name: string;
  /** Procedure(s) this label applies to — a label can belong to
   *  multiple (e.g. duodenal bulb shows in both EGD and Enteroscopy). */
  procedures: ProcedureType[];
}

/* =========================================================
 * Upper GI — EGD
 * 23-class GastroHUN + esophageal extensions.
 * ========================================================= */
export const ANATOMY_EGD: AnatomyLabel[] = [
  { id: "EGD_ESOP_UPPER",  name: "Upper oesophagus",                   procedures: ["EGD"] },
  { id: "EGD_ESOP_MID",    name: "Thoracic / mid oesophagus",          procedures: ["EGD"] },
  { id: "EGD_ESOP_LOWER",  name: "Distal oesophagus / GOJ",            procedures: ["EGD"] },
  { id: "EGD_A1", name: "Antrum · A1 (greater curve)",                 procedures: ["EGD"] },
  { id: "EGD_A2", name: "Antrum · A2 (posterior wall)",                procedures: ["EGD"] },
  { id: "EGD_A3", name: "Antrum · A3 (lesser curve)",                  procedures: ["EGD"] },
  { id: "EGD_A4", name: "Antrum · A4 (anterior wall)",                 procedures: ["EGD"] },
  { id: "EGD_A5", name: "Antrum · A5 (distal)",                        procedures: ["EGD"] },
  { id: "EGD_A6", name: "Antrum · A6 (angularis)",                     procedures: ["EGD"] },
  { id: "EGD_G1", name: "Gastric body · G1",                           procedures: ["EGD"] },
  { id: "EGD_G2", name: "Gastric body · G2",                           procedures: ["EGD"] },
  { id: "EGD_G3", name: "Gastric body · G3 (upper)",                   procedures: ["EGD"] },
  { id: "EGD_G4", name: "Gastric fundus · G4",                         procedures: ["EGD"] },
  { id: "EGD_L1", name: "Lower body · L1",                             procedures: ["EGD"] },
  { id: "EGD_L2", name: "Lower body · L2",                             procedures: ["EGD"] },
  { id: "EGD_L3", name: "Lower body · L3",                             procedures: ["EGD"] },
  { id: "EGD_L4", name: "Lower body · L4",                             procedures: ["EGD"] },
  { id: "EGD_L5", name: "Lower body · L5",                             procedures: ["EGD"] },
  { id: "EGD_L6", name: "Lower body · L6",                             procedures: ["EGD"] },
  { id: "EGD_P1", name: "Pylorus · P1 (opening)",                      procedures: ["EGD", "EUS"] },
  { id: "EGD_P2", name: "Pylorus · P2 (proximal)",                     procedures: ["EGD", "EUS"] },
  { id: "EGD_P3", name: "Pylorus · P3 (mid)",                          procedures: ["EGD", "EUS"] },
  { id: "EGD_P4", name: "Pylorus · P4 (distal)",                       procedures: ["EGD", "EUS"] },
  { id: "EGD_P5", name: "Duodenal bulb · D1",                          procedures: ["EGD", "EUS", "ERCP", "Enteroscopy"] },
  { id: "EGD_P6", name: "Post-bulbar duodenum · D2",                   procedures: ["EGD", "EUS", "ERCP", "Enteroscopy"] },
  { id: "EGD_OTH", name: "Other / transitional",                       procedures: ["EGD"] },
];

/* =========================================================
 * Lower GI — Colonoscopy + Sigmoidoscopy (subset)
 * ========================================================= */
export const ANATOMY_COLON: AnatomyLabel[] = [
  { id: "COL_TI",         name: "Terminal ileum",       procedures: ["Colonoscopy"] },
  { id: "COL_IC_VALVE",   name: "Ileocecal valve",      procedures: ["Colonoscopy"] },
  { id: "COL_CECUM",      name: "Caecum",               procedures: ["Colonoscopy"] },
  { id: "COL_APPENDIX",   name: "Appendiceal orifice",  procedures: ["Colonoscopy"] },
  { id: "COL_ASC",        name: "Ascending colon",      procedures: ["Colonoscopy"] },
  { id: "COL_HEP_FLEX",   name: "Hepatic flexure",      procedures: ["Colonoscopy"] },
  { id: "COL_TRANS",      name: "Transverse colon",     procedures: ["Colonoscopy"] },
  { id: "COL_SPL_FLEX",   name: "Splenic flexure",      procedures: ["Colonoscopy", "Sigmoidoscopy"] },
  { id: "COL_DESC",       name: "Descending colon",     procedures: ["Colonoscopy", "Sigmoidoscopy"] },
  { id: "COL_SIGMOID",    name: "Sigmoid colon",        procedures: ["Colonoscopy", "Sigmoidoscopy"] },
  { id: "COL_RECTOSIGM",  name: "Rectosigmoid junction",procedures: ["Colonoscopy", "Sigmoidoscopy"] },
  { id: "COL_RECTUM",     name: "Rectum",               procedures: ["Colonoscopy", "Sigmoidoscopy"] },
  { id: "COL_ANAL",       name: "Anal canal",           procedures: ["Colonoscopy", "Sigmoidoscopy"] },
];

/* =========================================================
 * Small bowel — Enteroscopy + Capsule endoscopy
 * ========================================================= */
export const ANATOMY_SMALL_BOWEL: AnatomyLabel[] = [
  { id: "SB_D1",    name: "Duodenum · D1 (bulb)",      procedures: ["Enteroscopy", "CapsuleEndoscopy"] },
  { id: "SB_D2",    name: "Duodenum · D2 (descending)",procedures: ["Enteroscopy", "CapsuleEndoscopy"] },
  { id: "SB_D3",    name: "Duodenum · D3 (horizontal)",procedures: ["Enteroscopy", "CapsuleEndoscopy"] },
  { id: "SB_D4",    name: "Duodenum · D4 (ascending)", procedures: ["Enteroscopy", "CapsuleEndoscopy"] },
  { id: "SB_LIG",   name: "Ligament of Treitz (DJ flexure)", procedures: ["Enteroscopy", "CapsuleEndoscopy"] },
  { id: "SB_JEJ_P", name: "Proximal jejunum",          procedures: ["Enteroscopy", "CapsuleEndoscopy"] },
  { id: "SB_JEJ_D", name: "Distal jejunum",            procedures: ["Enteroscopy", "CapsuleEndoscopy"] },
  { id: "SB_IL_P",  name: "Proximal ileum",            procedures: ["Enteroscopy", "CapsuleEndoscopy"] },
  { id: "SB_IL_D",  name: "Distal / terminal ileum",   procedures: ["Enteroscopy", "CapsuleEndoscopy"] },
];

/* =========================================================
 * Pancreatobiliary — ERCP
 * ========================================================= */
export const ANATOMY_ERCP: AnatomyLabel[] = [
  { id: "ERCP_AMP",     name: "Major papilla (ampulla of Vater)", procedures: ["ERCP", "EUS"] },
  { id: "ERCP_MIN_PAP", name: "Minor papilla",                    procedures: ["ERCP"] },
  { id: "ERCP_CBD_DIST",name: "Common bile duct · distal",        procedures: ["ERCP", "EUS"] },
  { id: "ERCP_CBD_MID", name: "Common bile duct · mid",           procedures: ["ERCP", "EUS"] },
  { id: "ERCP_CBD_PROX",name: "Common bile duct · proximal",      procedures: ["ERCP", "EUS"] },
  { id: "ERCP_CHD",     name: "Common hepatic duct",              procedures: ["ERCP"] },
  { id: "ERCP_RHD",     name: "Right hepatic duct",               procedures: ["ERCP"] },
  { id: "ERCP_LHD",     name: "Left hepatic duct",                procedures: ["ERCP"] },
  { id: "ERCP_IHD_R",   name: "Intrahepatic ducts · right",       procedures: ["ERCP"] },
  { id: "ERCP_IHD_L",   name: "Intrahepatic ducts · left",        procedures: ["ERCP"] },
  { id: "ERCP_CYS",     name: "Cystic duct",                      procedures: ["ERCP"] },
  { id: "ERCP_GB",      name: "Gallbladder",                      procedures: ["ERCP", "EUS"] },
  { id: "ERCP_PD_HEAD", name: "Pancreatic duct · head",           procedures: ["ERCP", "EUS"] },
  { id: "ERCP_PD_BODY", name: "Pancreatic duct · body",           procedures: ["ERCP", "EUS"] },
  { id: "ERCP_PD_TAIL", name: "Pancreatic duct · tail",           procedures: ["ERCP", "EUS"] },
];

/* =========================================================
 * Endoscopic ultrasound — EUS
 *   Primarily the upper-GI + pancreatobiliary landmarks above,
 *   plus nodal stations sonographers call out.
 * ========================================================= */
export const ANATOMY_EUS_ONLY: AnatomyLabel[] = [
  { id: "EUS_PANCREAS_H", name: "Pancreas · head",                procedures: ["EUS"] },
  { id: "EUS_PANCREAS_U", name: "Pancreas · uncinate",            procedures: ["EUS"] },
  { id: "EUS_PANCREAS_N", name: "Pancreas · neck",                procedures: ["EUS"] },
  { id: "EUS_PANCREAS_B", name: "Pancreas · body",                procedures: ["EUS"] },
  { id: "EUS_PANCREAS_T", name: "Pancreas · tail",                procedures: ["EUS"] },
  { id: "EUS_LIVER_L",    name: "Liver · left lobe",              procedures: ["EUS"] },
  { id: "EUS_ADRENAL_L",  name: "Left adrenal",                   procedures: ["EUS"] },
  { id: "EUS_NODE_4L",    name: "Node station 4L (paratracheal)", procedures: ["EUS"] },
  { id: "EUS_NODE_7",     name: "Node station 7 (subcarinal)",    procedures: ["EUS", "Bronchoscopy"] },
  { id: "EUS_NODE_8",     name: "Node station 8 (para-oesoph.)",  procedures: ["EUS"] },
  { id: "EUS_NODE_9",     name: "Node station 9 (pulm lig.)",     procedures: ["EUS"] },
];

/* =========================================================
 * Airway — Bronchoscopy (ATS bronchial nomenclature)
 * ========================================================= */
export const ANATOMY_AIRWAY: AnatomyLabel[] = [
  { id: "AIR_SG",     name: "Supraglottis / larynx",          procedures: ["Bronchoscopy", "Laryngoscopy"] },
  { id: "AIR_VC",     name: "Vocal cords",                    procedures: ["Bronchoscopy", "Laryngoscopy"] },
  { id: "AIR_SUBGL",  name: "Subglottis",                     procedures: ["Bronchoscopy", "Laryngoscopy"] },
  { id: "AIR_TR_U",   name: "Upper trachea",                  procedures: ["Bronchoscopy"] },
  { id: "AIR_TR_M",   name: "Mid trachea",                    procedures: ["Bronchoscopy"] },
  { id: "AIR_TR_L",   name: "Distal trachea",                 procedures: ["Bronchoscopy"] },
  { id: "AIR_CARINA", name: "Main carina",                    procedures: ["Bronchoscopy"] },
  { id: "AIR_RMB",    name: "Right main bronchus",            procedures: ["Bronchoscopy"] },
  { id: "AIR_RUL",    name: "Right upper lobe (RUL) · B1-3",  procedures: ["Bronchoscopy"] },
  { id: "AIR_BI",     name: "Bronchus intermedius",           procedures: ["Bronchoscopy"] },
  { id: "AIR_RML",    name: "Right middle lobe (RML) · B4-5", procedures: ["Bronchoscopy"] },
  { id: "AIR_RLL",    name: "Right lower lobe (RLL) · B6-10", procedures: ["Bronchoscopy"] },
  { id: "AIR_LMB",    name: "Left main bronchus",             procedures: ["Bronchoscopy"] },
  { id: "AIR_LUL",    name: "Left upper lobe (LUL) · B1-3+lingula", procedures: ["Bronchoscopy"] },
  { id: "AIR_LLL",    name: "Left lower lobe (LLL) · B6-10",  procedures: ["Bronchoscopy"] },
];

/* =========================================================
 * Urology — Cystoscopy
 * ========================================================= */
export const ANATOMY_URINARY: AnatomyLabel[] = [
  { id: "URO_URETHRA_A",  name: "Anterior urethra",          procedures: ["Cystoscopy"] },
  { id: "URO_URETHRA_M",  name: "Membranous urethra",        procedures: ["Cystoscopy"] },
  { id: "URO_URETHRA_P",  name: "Prostatic urethra",         procedures: ["Cystoscopy"] },
  { id: "URO_BLADDER_NK", name: "Bladder neck",              procedures: ["Cystoscopy"] },
  { id: "URO_TRIGONE",    name: "Trigone",                   procedures: ["Cystoscopy"] },
  { id: "URO_UO_R",       name: "Right ureteric orifice",    procedures: ["Cystoscopy"] },
  { id: "URO_UO_L",       name: "Left ureteric orifice",     procedures: ["Cystoscopy"] },
  { id: "URO_DOME",       name: "Bladder dome",              procedures: ["Cystoscopy"] },
  { id: "URO_WALL_R",     name: "Right lateral wall",        procedures: ["Cystoscopy"] },
  { id: "URO_WALL_L",     name: "Left lateral wall",         procedures: ["Cystoscopy"] },
  { id: "URO_POST",       name: "Posterior wall",            procedures: ["Cystoscopy"] },
  { id: "URO_ANT",        name: "Anterior wall",             procedures: ["Cystoscopy"] },
];

/* =========================================================
 * Gynaecology — Hysteroscopy
 * ========================================================= */
export const ANATOMY_GYN: AnatomyLabel[] = [
  { id: "GYN_CERVIX",     name: "Ectocervix / external os",  procedures: ["Hysteroscopy"] },
  { id: "GYN_ENDOCX",     name: "Endocervical canal",        procedures: ["Hysteroscopy"] },
  { id: "GYN_INTERNAL_OS",name: "Internal os",               procedures: ["Hysteroscopy"] },
  { id: "GYN_LUS",        name: "Lower uterine segment",     procedures: ["Hysteroscopy"] },
  { id: "GYN_ANT",        name: "Anterior wall",             procedures: ["Hysteroscopy"] },
  { id: "GYN_POST",       name: "Posterior wall",            procedures: ["Hysteroscopy"] },
  { id: "GYN_LAT_R",      name: "Right lateral wall",        procedures: ["Hysteroscopy"] },
  { id: "GYN_LAT_L",      name: "Left lateral wall",         procedures: ["Hysteroscopy"] },
  { id: "GYN_FUNDUS",     name: "Fundus",                    procedures: ["Hysteroscopy"] },
  { id: "GYN_OSTIUM_R",   name: "Right tubal ostium",        procedures: ["Hysteroscopy"] },
  { id: "GYN_OSTIUM_L",   name: "Left tubal ostium",         procedures: ["Hysteroscopy"] },
];

/* =========================================================
 * Union + per-procedure filter
 * ========================================================= */
export const ANATOMY_ALL: AnatomyLabel[] = [
  ...ANATOMY_EGD,
  ...ANATOMY_COLON,
  ...ANATOMY_SMALL_BOWEL,
  ...ANATOMY_ERCP,
  ...ANATOMY_EUS_ONLY,
  ...ANATOMY_AIRWAY,
  ...ANATOMY_URINARY,
  ...ANATOMY_GYN,
];

export function anatomyForProcedure(procedure: ProcedureType): AnatomyLabel[] {
  return ANATOMY_ALL.filter((a) => a.procedures.includes(procedure));
}

/* =========================================================
 * Lesion labels — per-procedure filtered vocabulary.
 *
 * The catalogue is WEO-aligned where a code exists (MST 3.0 for
 * GI, ERS/STN for inflammatory / stones) and editorial-consensus
 * for specialty-specific findings (e.g. hysteroscopy myoma, cysto
 * papilloma, bronchoscopy carcinoma-in-situ). Colour palette is
 * chosen for contrast against mucosa (pink/red backgrounds) —
 * cool tones for structural findings, warm for acute/bleeding.
 *
 * Each label declares the procedures it's clinically relevant to.
 * "Normal / no finding" is global so every procedure has a negative
 * exit in the dropdown.
 * ========================================================= */
export interface LesionLabel {
  id: string;
  name: string;
  /** WEO MST 3.0 / IEC 60601 clinical code, where defined. */
  code?: string;
  /** Default colour for bounding-box / point markers. */
  color: string;
  /** Procedures this lesion is clinically relevant to. */
  procedures: ProcedureType[];
}

const GI_UPPER:  ProcedureType[] = ["EGD", "EUS"];
const GI_LOWER:  ProcedureType[] = ["Colonoscopy", "Sigmoidoscopy"];
const GI_SMALL:  ProcedureType[] = ["Enteroscopy", "CapsuleEndoscopy"];
const GI_ALL:    ProcedureType[] = [...GI_UPPER, ...GI_LOWER, ...GI_SMALL];
const ALL_PROCS: ProcedureType[] = [...PROCEDURE_TYPES];

export const LESIONS: LesionLabel[] = [
  /* --- GI-wide (upper + lower + small-bowel) --------------------- */
  { id: "polyp",          name: "Polyp",               code: "WEO-POL", color: "#f59e0b",
    procedures: [...GI_UPPER, ...GI_LOWER, ...GI_SMALL] },
  { id: "adenoma",        name: "Adenoma (suspected)", code: "WEO-ADN", color: "#dc2626",
    procedures: [...GI_UPPER, ...GI_LOWER, ...GI_SMALL] },
  { id: "ulcer",          name: "Ulcer",               code: "WEO-ULC", color: "#ef4444",
    procedures: [...GI_ALL, "Cystoscopy", "Laryngoscopy"] },
  { id: "erosion",        name: "Erosion",             code: "WEO-ERS", color: "#f97316",
    procedures: [...GI_ALL, "Laryngoscopy"] },
  { id: "bleeding",       name: "Active bleeding",     code: "WEO-HEM", color: "#b91c1c",
    procedures: ALL_PROCS },
  { id: "mass",           name: "Mass / tumour",       code: "WEO-MAS", color: "#991b1b",
    procedures: ALL_PROCS },
  { id: "inflammation",   name: "Inflammation",        code: "WEO-INF", color: "#fb7185",
    procedures: ALL_PROCS },
  { id: "stricture",      name: "Stricture",           code: "WEO-STR", color: "#8b5cf6",
    procedures: [...GI_ALL, "ERCP", "Bronchoscopy", "Cystoscopy", "Laryngoscopy"] },
  { id: "diverticulum",   name: "Diverticulum",        code: "WEO-DIV", color: "#0ea5e9",
    procedures: [...GI_ALL, "Cystoscopy"] },
  { id: "foreign_body",   name: "Foreign body",        code: "WEO-FBO", color: "#64748b",
    procedures: ALL_PROCS },

  /* --- Upper-GI specific ----------------------------------------- */
  { id: "varices",        name: "Varices",             code: "WEO-VAR", color: "#7c3aed",
    procedures: [...GI_UPPER, "Laryngoscopy"] },
  { id: "barretts",       name: "Barrett's oesophagus",code: "WEO-BAR", color: "#e11d48",
    procedures: ["EGD"] },
  { id: "hiatus_hernia",  name: "Hiatus hernia",       code: "WEO-HHR", color: "#f472b6",
    procedures: ["EGD"] },
  { id: "h_pylori",       name: "H. pylori (suspected)", code: "WEO-HPY", color: "#fda4af",
    procedures: ["EGD"] },

  /* --- Lower-GI specific ----------------------------------------- */
  { id: "haemorrhoid",    name: "Haemorrhoid",         code: "WEO-HMR", color: "#c026d3",
    procedures: GI_LOWER },
  { id: "paris_is",       name: "Polyp · Paris Is (sessile)",  color: "#fb923c", procedures: GI_LOWER },
  { id: "paris_ip",       name: "Polyp · Paris Ip (pedunculated)", color: "#f59e0b", procedures: GI_LOWER },
  { id: "paris_lst",      name: "Polyp · Paris LST",           color: "#fbbf24", procedures: GI_LOWER },
  { id: "ibd_uc",         name: "UC-pattern inflammation",     color: "#f43f5e", procedures: GI_LOWER },
  { id: "ibd_cd",         name: "Crohn-pattern inflammation",  color: "#e11d48", procedures: GI_LOWER },

  /* --- Small-bowel specific -------------------------------------- */
  { id: "angiodysplasia", name: "Angiodysplasia",      code: "WEO-AGD", color: "#a855f7",
    procedures: GI_SMALL },
  { id: "lymphangiect",   name: "Lymphangiectasia",    color: "#60a5fa",
    procedures: GI_SMALL },

  /* --- ERCP / pancreatobiliary ----------------------------------- */
  { id: "stone_biliary",  name: "Biliary stone",       code: "WEO-STN", color: "#22d3ee",
    procedures: ["ERCP", "EUS"] },
  { id: "stone_pd",       name: "Pancreatic duct stone",                color: "#06b6d4",
    procedures: ["ERCP", "EUS"] },
  { id: "papillary_tum",  name: "Ampullary tumour",                     color: "#be123c",
    procedures: ["ERCP", "EUS"] },

  /* --- Bronchoscopy ---------------------------------------------- */
  { id: "nodule",         name: "Nodule",              code: "WEO-NOD", color: "#eab308",
    procedures: ["Bronchoscopy", "EUS"] },
  { id: "carcinoma_in_situ", name: "Carcinoma-in-situ",                 color: "#be185d",
    procedures: ["Bronchoscopy", "Laryngoscopy"] },
  { id: "secretions",     name: "Secretions / pus",                     color: "#14b8a6",
    procedures: ["Bronchoscopy"] },
  { id: "granuloma",      name: "Granuloma",                            color: "#f472b6",
    procedures: ["Bronchoscopy", "Laryngoscopy"] },
  { id: "mucosal_oedema", name: "Mucosal oedema",                       color: "#fb7185",
    procedures: ["Bronchoscopy", "Laryngoscopy", "Cystoscopy"] },

  /* --- Cystoscopy ------------------------------------------------ */
  { id: "bladder_stone",  name: "Bladder stone",                        color: "#22d3ee",
    procedures: ["Cystoscopy"] },
  { id: "papilloma",      name: "Papilloma",                            color: "#d946ef",
    procedures: ["Cystoscopy", "Laryngoscopy"] },
  { id: "trabeculation",  name: "Trabeculation",                        color: "#818cf8",
    procedures: ["Cystoscopy"] },
  { id: "urothelial_ca",  name: "Urothelial carcinoma",                 color: "#be123c",
    procedures: ["Cystoscopy"] },

  /* --- Hysteroscopy ---------------------------------------------- */
  { id: "endo_polyp",     name: "Endometrial polyp",                    color: "#f59e0b",
    procedures: ["Hysteroscopy"] },
  { id: "myoma",          name: "Myoma / fibroid",                      color: "#dc2626",
    procedures: ["Hysteroscopy"] },
  { id: "synechia",       name: "Synechia / adhesion",                  color: "#6366f1",
    procedures: ["Hysteroscopy"] },
  { id: "septum",         name: "Uterine septum",                       color: "#8b5cf6",
    procedures: ["Hysteroscopy"] },
  { id: "endo_ca",        name: "Endometrial carcinoma",                color: "#be123c",
    procedures: ["Hysteroscopy"] },

  /* --- Laryngoscopy ---------------------------------------------- */
  { id: "vocal_polyp",    name: "Vocal cord polyp",                     color: "#f59e0b",
    procedures: ["Laryngoscopy"] },
  { id: "leukoplakia",    name: "Leukoplakia",                          color: "#f5f5f4",
    procedures: ["Laryngoscopy"] },
  { id: "vocal_nodule",   name: "Vocal cord nodule",                    color: "#fbbf24",
    procedures: ["Laryngoscopy"] },
  { id: "cord_paralysis", name: "Vocal cord paralysis",                 color: "#6366f1",
    procedures: ["Laryngoscopy"] },

  /* --- Universal negative case ----------------------------------- */
  { id: "normal",         name: "Normal / no finding",                  color: "#22c55e",
    procedures: ALL_PROCS },
];

export function lesionsForProcedure(procedure: ProcedureType): LesionLabel[] {
  return LESIONS.filter((l) => l.procedures.includes(procedure));
}

/** Colour assignment for the anatomy-span bar on the timeline. */
const SPAN_COLORS = [
  "#27a567", "#3b82f6", "#8b5cf6", "#f59e0b", "#ec4899",
  "#0ea5e9", "#10b981", "#f97316", "#6366f1", "#14b8a6",
];
export function colorForSpan(i: number) {
  return SPAN_COLORS[i % SPAN_COLORS.length];
}
